import { factsFromSchema, parseFacts } from './facts'
import type { OrgWorkflow, WorkflowRecord, WorkflowView } from './types'

function nonEmpty(value: string | null | undefined): string | null {
  const trimmed = value?.trim()
  return trimmed ? (value ?? null) : null
}

/**
 * Join Nebula's org catalogue to the `app_nova_workflows` rows by slug.
 *
 * Nebula defines the list: it reads the workflow YAML on disk, so it is the only
 * source that knows what the org can actually run. Rows that exist only in the db
 * (a workflow deleted from the nova repo, or one registered for another org) are
 * dropped — they aren't runnable, so listing them would misreport the org's
 * capabilities. The db row supplies what Nebula doesn't track: `trigger`,
 * session counts, and the duty links (whose own `active` flags say whether the
 * workflow is still switched on anywhere).
 *
 * Nebula wins on the metadata both sources carry (title, description, facts)
 * because the db copy is written by nova's register from the same YAML and then
 * drifts — nothing in Hoku edits those columns, and `app_nova_workflows` is on
 * its way out, so there is no local edit to preserve.
 *
 * A deprecated workflow is kept, not dropped: Nebula still lists it because the
 * YAML stays on disk, and the row is what keeps its historical sessions
 * reachable from this page. It carries `deprecated` through so the UI can say
 * the workflow is retired instead of implying it can still be run, and sorts to
 * the bottom so the live catalogue reads first.
 */
export function mergeWorkflows(
  catalogue: OrgWorkflow[],
  records: WorkflowRecord[]
): WorkflowView[] {
  const bySlug = new Map(records.map((record) => [record.slug, record]))

  const merged = catalogue.map((workflow) => {
    const record = bySlug.get(workflow.slug)
    const schema = workflow.facts_schema
    const duties = record?.duties ?? []
    // A non-null schema is authoritative even when empty — that means "this
    // workflow takes no facts". Null means Nebula couldn't read the YAML, which
    // is the only case where the db text column is worth reading (and the only
    // case where it should survive as a raw-text fallback in the UI).
    const factsText = schema ? '' : (record?.facts ?? '')

    return {
      key: record?.id ?? workflow.slug,
      slug: workflow.slug,
      title:
        nonEmpty(workflow.title) ?? nonEmpty(record?.title) ?? workflow.slug,
      description:
        nonEmpty(workflow.description) ?? nonEmpty(record?.description) ?? '',
      // Nebula's org listing never resolves a trigger (it lives on the
      // workflow-duty link), so this only ever comes from the db row.
      trigger: nonEmpty(record?.trigger),
      // Off only once every duty holding this workflow has switched it off. A
      // workflow with no links isn't off — nothing has turned it off — and nor
      // is an unregistered one, which Nebula wouldn't list if it couldn't run.
      // `record.active` is not consulted: nova never writes that column.
      active: duties.length === 0 || duties.some((duty) => duty.active),
      deprecated: workflow.deprecated,
      facts: schema ? factsFromSchema(schema) : parseFacts(factsText),
      factsText,
      // Null, not 0, without a db row: sessions are keyed by org and slug on
      // their own, so an unregistered workflow can have runs this page can't
      // count. Claiming zero would be worse than saying nothing.
      sessionCount: record?.session_count ?? null,
      duties,
      unregistered: !record,
    }
  })

  // Retired workflows sink below the runnable ones — they're history, not
  // capability. The sort is stable, so Nebula's catalogue order holds within
  // each group.
  return merged.toSorted((a, b) => Number(a.deprecated) - Number(b.deprecated))
}
